<template>
  <div
    ref="containerRef"
    class="relative flex items-center justify-center w-full h-full overflow-hidden"
  >
    <div
      class="pt-4 pb-11.5 absolute left-2 h-full flex flex-col justify-between text-s-medium text-black-white"
    >
      <div>1.6mm</div>
      <div>3.0mm</div>
    </div>

    <div
      class="pt-4 pb-11.5 absolute right-3 h-full flex flex-col justify-between text-s-medium text-black-white"
    >
      <div>1.6mm</div>
      <div>3.0mm</div>
    </div>

    <svg
      :width="svgSize.width"
      :height="svgSize.height"
      :viewBox="`0 0 ${svgSize.width} ${svgSize.height}`"
      class="select-none overflow-visible"
      preserveAspectRatio="xMidYMid meet"
    >
      <!-- U形轨道 -->
      <path
        ref="pathRef"
        :d="U_PATH"
        fill="none"
        stroke="#ffffff"
        :stroke-width="TRACK_WIDTH"
        stroke-linecap="round"
      />

      <!-- 分段轨道填充：无延伸时不渲染，避免 stroke-linecap=round 在零长度时画出圆点 -->
      <template v-for="(seg, i) in segments" :key="`seg-${i}`">
        <path
          v-if="hasSegmentFill(i)"
          :d="U_PATH"
          fill="none"
          stroke-linecap="round"
          class="z-1"
          :stroke-width="TRACK_WIDTH"
          :stroke-dasharray="getSegDashArray(i)"
          :stroke-dashoffset="getSegDashOffset(i)"
          :class="seg.trackColor"
        />
      </template>

      <!-- 引导线 -->
      <g class="pointer-events-none">
        <path
          :d="INNER_GUIDE_PATH"
          fill="none"
          stroke-width="1.5"
          stroke-linecap="round"
          class="stroke-brand-blue1"
        />
        <polygon
          :points="`${L_INNER_X - 4},${START_Y - 17} ${L_INNER_X + 4},${START_Y - 17} ${L_INNER_X},${START_Y - 11}`"
          fill="#3b82f6"
        />
        <polygon
          :points="`${R_INNER_X},${START_Y - 18} ${R_INNER_X - 4},${START_Y - 12} ${R_INNER_X + 4},${START_Y - 12}`"
          fill="#3b82f6"
        />
      </g>

      <!-- 拖拽区域：被覆盖节点不渲染 -->
      <template v-for="(_, i) in segments.slice(0, 3)" :key="`handle-${i}`">
        <g
          v-if="!isNodeCovered(i)"
          :transform="`translate(${getPointAtProgress(progress[i]).x}, ${getPointAtProgress(progress[i]).y}) rotate(${getAngleAtProgress(progress[i])})`"
          @mousedown="canInteractNode(i) && handleMouseDown(i)"
        >
          <polygon
            :points="`${20},${0} ${12},${-6} ${12},${6}`"
            fill="#1a1a1a"
            :opacity="getHandleOpacity(i)"
            style="transition: opacity 0.1s"
          />
          <rect
            :x="10"
            :y="-TRACK_WIDTH / 2"
            :width="25"
            :height="TRACK_WIDTH"
            :class="canInteractNode(i) ? CURSORS[i] : 'cursor-default'"
            fill="transparent"
          />
        </g>
      </template>

      <!-- 圆形节点：被覆盖节点不渲染 -->
      <template v-for="(seg, i) in segments" :key="`anchor-${i}`">
        <g
          v-if="!isNodeCovered(i)"
          :transform="`translate(${getPointAtProgress(NODE_ANCHORS[i]).x}, ${getPointAtProgress(NODE_ANCHORS[i]).y})`"
          class="cursor-pointer"
        >
          <circle
            :r="TRACK_WIDTH / 2"
            :stroke-width="1"
            :class="getNodeCircleClass(seg, activeNodeIndex === i, nodeKeys(i).length > 0)"
          />
          <text
            v-if="nodeKeys(i).length"
            x="0"
            y="0"
            text-anchor="middle"
            dominant-baseline="middle"
            class="pointer-events-none select-none text-text-important-default text-s-medium"
          >
            <tspan
              v-for="(line, lineIndex) in getNodeTextLines(nodeKeys(i))"
              :key="`label-${i}-${lineIndex}`"
              x="0"
              :dy="getNodeLineDy(nodeKeys(i), lineIndex)"
            >
              {{ line }}
            </tspan>
          </text>
        </g>
      </template>
    </svg>

    <!-- HTML drop 覆盖层，SVG 不支持原生 drag/drop -->
    <template v-for="(_, i) in segments" :key="`drop-${i}`">
      <Tooltip
        v-if="!isNodeCovered(i)"
        :delay-duration="300"
        :side-offset="-4"
        side="bottom"
        align="start"
      >
        <div
          class="absolute rounded-full cursor-pointer"
          :style="getNodeOverlayStyle(i)"
          @click="handleNodeClick(i)"
          @dragover="handleDragOver($event, i)"
          @drop="handleDrop($event, i)"
        />
        <template #content>
          <div
            v-if="props.nodeKeys[i]?.length"
            class="flex flex-col gap-0.5 p-1 glass-frosted rounded-lg"
          >
            <Button
              v-for="(key, ki) in props.nodeKeys[i]"
              :key="ki"
              color="black"
              variant="frameless"
              size="xs"
              @click.stop="emit('node-remove', { nodeIndex: i, keyIndex: ki })"
            >
              <span>{{ $t('common.delete') }} {{ getKeyLabel(key.keywords) }}</span>
            </Button>
          </div>
        </template>
      </Tooltip>
    </template>
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted, onUnmounted, reactive, ref, watch } from 'vue'
  import { getDragData, DragType } from '@/utils/dnd'
  import { Tooltip, Button } from '@rapoo/element'
  import { getLocalizedField, KEYCODE_KEYWORDS_MAP } from '../../../utils/keyMapSettings'

  type KeyData = { keywords: string }

  const props = defineProps<{
    nodeKeys: KeyData[][]
    activeNodeIndex: number
  }>()

  const emit = defineEmits<{
    (e: 'node-click', index: number): void
    (e: 'node-drop', payload: { nodeIndex: number; keyData: KeyData }): void
    (e: 'node-remove', payload: { nodeIndex: number; keyIndex: number }): void
    (e: 'covered-change', indexes: number[]): void
  }>()

  // 节段调色板：每个 DKS 区段的轨道色、边框色、填充色
  type SegmentPalette = {
    trackColor: string
    borderColor: string
    fillColor: string
  }

  type DksSegment = SegmentPalette

  // ── 轨道几何常量 ──────────────────────────────────────────────
  const TRACK_WIDTH = 54
  const RADIUS = 68
  const U_WIDTH_REDUCTION = 8
  const LEFT_X = 82
  const RIGHT_X = LEFT_X + RADIUS * 2 - U_WIDTH_REDUCTION
  const START_Y = 28
  const END_Y = 102

  // ── 内部引导线几何常量 ────────────────────────────────────────
  const INNER_GUIDE_RADIUS = 30
  const INNER_GUIDE_WIDTH_REDUCTION = 8
  const CENTER_X = (LEFT_X + RIGHT_X) / 2
  const L_INNER_X = CENTER_X - INNER_GUIDE_RADIUS + INNER_GUIDE_WIDTH_REDUCTION / 2
  const R_INNER_X = CENTER_X + INNER_GUIDE_RADIUS - INNER_GUIDE_WIDTH_REDUCTION / 2
  const REFERENCE_BOTTOM_FLAT_RATIO = 19 / 129
  const BEZIER_KAPPA = 0.5522847498

  function buildRoundedUPath(leftX: number, rightX: number, topY: number, bottomY: number): string {
    const totalWidth = rightX - leftX
    const flatWidth = totalWidth * REFERENCE_BOTTOM_FLAT_RATIO
    const cornerRadius = (totalWidth - flatWidth) / 2
    const curveStartY = bottomY - cornerRadius
    const flatLeftX = leftX + cornerRadius
    const flatRightX = rightX - cornerRadius
    const controlOffset = cornerRadius * BEZIER_KAPPA

    return [
      `M ${leftX} ${topY}`,
      `V ${curveStartY}`,
      `C ${leftX} ${curveStartY + controlOffset} ${flatLeftX - controlOffset} ${bottomY} ${flatLeftX} ${bottomY}`,
      `H ${flatRightX}`,
      `C ${flatRightX + controlOffset} ${bottomY} ${rightX} ${curveStartY + controlOffset} ${rightX} ${curveStartY}`,
      `V ${topY}`,
    ].join(' ')
  }

  const INNER_GUIDE_BOTTOM_Y = END_Y + INNER_GUIDE_RADIUS
  const INNER_GUIDE_PATH = buildRoundedUPath(
    L_INNER_X,
    R_INNER_X,
    START_Y - 16,
    INNER_GUIDE_BOTTOM_Y,
  )

  const U_BOTTOM_Y = END_Y + RADIUS
  const U_PATH = buildRoundedUPath(LEFT_X, RIGHT_X, START_Y, U_BOTTOM_Y)

  const NODE_ANCHORS = [0, 0.31, 0.69, 1] as const
  const CURSORS = ['cursor-row-resize', 'cursor-col-resize', 'cursor-row-resize', 'cursor-default']

  const SEGMENT_PALETTE: SegmentPalette[] = [
    {
      trackColor: 'stroke-blue-light-hover',
      borderColor: 'stroke-brand-blue1',
      fillColor: 'fill-blue-light-default',
    },
    {
      trackColor: 'stroke-red-hover',
      borderColor: 'stroke-brand-red',
      fillColor: 'fill-red-default',
    },
    {
      trackColor: 'stroke-orange-hover',
      borderColor: 'stroke-brand-orange',
      fillColor: 'fill-orange-default',
    },
    {
      trackColor: 'stroke-purple-hover',
      borderColor: 'stroke-purple-active',
      fillColor: 'fill-purple-default',
    },
  ]

  const segments = reactive<DksSegment[]>(SEGMENT_PALETTE.map((p) => ({ ...p })))

  // 每个节点对应 nodeKeys prop 中的按键标签列表
  const nodeKeys = (i: number): string[] => {
    return (props.nodeKeys[i] ?? []).map((k) => getKeyLabel(k.keywords))
  }

  const containerRef = ref<HTMLDivElement | null>(null)
  const pathRef = ref<SVGPathElement | null>(null)
  const svgSize = reactive({ width: 0, height: 0 })
  const pathLength = ref(0)
  const progress = ref<number[]>([...NODE_ANCHORS])
  const isDragging = ref<number | null>(null)

  /**
   * 手柄 handleIdx 当前拖拽覆盖到的最远节点下标；未拖拽返回 null。
   * 被覆盖的是中间节点 (handleIdx, coverEnd)，释放终点 coverEnd 本身仍可填写。
   */
  const getDragCoverEnd = (handleIdx: number): number | null => {
    const currentP = progress.value[handleIdx] ?? NODE_ANCHORS[handleIdx]
    const anchorP = NODE_ANCHORS[handleIdx]
    if (Math.abs(currentP - anchorP) < 0.02) return null

    for (let j = handleIdx + 1; j < NODE_ANCHORS.length; j++) {
      const snapTarget = NODE_ANCHORS[j] - 0.16
      if (Math.abs(currentP - snapTarget) < 0.02) return j
    }

    for (let j = NODE_ANCHORS.length - 1; j > handleIdx; j--) {
      if (currentP >= NODE_ANCHORS[j] - 0.16 - 0.02) return j
    }

    return null
  }

  /** 被前方节点拖拽范围跨过的中间节点集合（不含释放终点） */
  const coveredNodeSet = computed(() => {
    const covered = new Set<number>()
    for (let i = 0; i < NODE_ANCHORS.length - 1; i++) {
      const coverEnd = getDragCoverEnd(i)
      if (coverEnd == null) continue
      // 仅禁用中间被跨过的节点，释放终点仍可填写/交互
      for (let j = i + 1; j < coverEnd; j++) {
        covered.add(j)
      }
    }
    return covered
  })

  const isNodeCovered = (nodeIdx: number) => coveredNodeSet.value.has(nodeIdx)

  /** 节点是否可交互（有键且未被覆盖） */
  const canInteractNode = (nodeIdx: number) =>
    nodeKeys(nodeIdx).length > 0 && !isNodeCovered(nodeIdx)

  const getHandleOpacity = (i: number) => {
    if (isDragging.value === i) return 1
    if (nodeKeys(i).length > 0) return 0.8
    return 0.3
  }

  watch(
    coveredNodeSet,
    (set) => {
      emit(
        'covered-change',
        [...set].sort((a, b) => a - b),
      )
    },
    { immediate: true },
  )

  /** 槽内按键全部清空时，将该节点 track 复位到初始锚点 */
  watch(
    () => props.nodeKeys.map((keys) => keys.length),
    (lengths, prevLengths) => {
      if (!prevLengths) return
      const next = [...progress.value]
      let changed = false
      for (let i = 0; i < NODE_ANCHORS.length; i++) {
        if (lengths[i] === 0 && (prevLengths[i] ?? 0) > 0) {
          const anchor = NODE_ANCHORS[i]
          if (Math.abs(next[i] - anchor) > 0.001) {
            next[i] = anchor
            changed = true
          }
        }
      }
      if (changed) progress.value = next
    },
  )

  onMounted(() => {
    if (!containerRef.value) return
    svgSize.width = containerRef.value.offsetWidth
    svgSize.height = containerRef.value.offsetHeight
    if (pathRef.value) {
      pathLength.value = pathRef.value.getTotalLength()
    }
  })

  onUnmounted(() => {
    removeListeners()
  })

  function getPointAtProgress(p: number): { x: number; y: number } {
    if (!pathRef.value || pathLength.value === 0) return { x: LEFT_X, y: START_Y }
    const pt = pathRef.value.getPointAtLength(p * pathLength.value)
    return { x: pt.x, y: pt.y }
  }

  function getAngleAtProgress(p: number): number {
    if (!pathRef.value || pathLength.value === 0) return 0
    const length = p * pathLength.value
    const p1 = pathRef.value.getPointAtLength(Math.max(0, length - 1))
    const p2 = pathRef.value.getPointAtLength(Math.min(pathLength.value, length + 1))
    return Math.atan2(p2.y - p1.y, p2.x - p1.x) * (180 / Math.PI)
  }

  function hasSegmentFill(i: number): boolean {
    return progress.value[i] > NODE_ANCHORS[i] + 0.001
  }

  function getSegDashArray(i: number): string {
    const startAnchor = NODE_ANCHORS[i]
    const endP = progress.value[i]
    const len = (endP - startAnchor) * pathLength.value
    if (len <= 0) return `0 ${pathLength.value}`
    return `${len} ${pathLength.value}`
  }

  function getSegDashOffset(i: number): number {
    return -NODE_ANCHORS[i] * pathLength.value
  }

  function getNodeCircleClass(seg: DksSegment, active: boolean, hasKeys: boolean): string {
    if (active) return `${seg.fillColor} ${seg.borderColor}`
    if (hasKeys) return seg.fillColor
    return 'stroke-n-light-default fill-[#f2f2f2]'
  }

  function getNodeTextLines(keys: string[]): string[] {
    if (keys.length <= 2) return [...keys]
    const lines: string[] = []
    for (let i = 0; i < keys.length; i += 2) lines.push(keys.slice(i, i + 2).join(' '))
    return lines
  }

  function getNodeLineDy(keys: string[], lineIndex: number): number {
    const lineCount = getNodeTextLines(keys).length
    if (lineCount === 1) return 0
    return lineIndex === 0 ? -5 : 10
  }

  function getNodeOverlayStyle(i: number) {
    const { x, y } = getPointAtProgress(NODE_ANCHORS[i])
    const size = TRACK_WIDTH - 16
    return {
      left: `${x - size / 2}px`,
      top: `${y - size / 2}px`,
      width: `${size}px`,
      height: `${size}px`,
    }
  }

  function handleNodeClick(index: number) {
    if (isNodeCovered(index)) return
    emit('node-click', index)
  }

  function handleDragOver(event: DragEvent, nodeIndex: number) {
    if (isNodeCovered(nodeIndex)) return
    event.preventDefault()
  }

  function handleDrop(event: DragEvent, nodeIndex: number) {
    if (isNodeCovered(nodeIndex)) return
    if (!event.dataTransfer) return
    const keyData = getDragData<KeyData>(event.dataTransfer, DragType.KEY_MAPPING)

    if (!keyData) return
    emit('node-drop', { nodeIndex, keyData })
  }

  function handleMouseDown(index: number) {
    if (!canInteractNode(index)) return
    isDragging.value = index
    addListeners()
  }

  function getMaxReachable(idx: number): number {
    let endAnchorIdx = idx + 1
    while (
      endAnchorIdx < segments.length - 1 &&
      (props.nodeKeys[endAnchorIdx] ?? []).length === 0
    ) {
      endAnchorIdx++
    }
    for (let i = idx + 1; i <= endAnchorIdx; i++) {
      if (progress.value[i] > NODE_ANCHORS[i] + 0.001) {
        endAnchorIdx = i
        break
      }
    }
    const endRange = endAnchorIdx < NODE_ANCHORS.length ? (NODE_ANCHORS[endAnchorIdx] as number) : 1
    return Math.max(NODE_ANCHORS[idx] + 0.01, endRange - 0.16)
  }

  function getSnapAnchors(idx: number): number[] {
    const anchors: number[] = [NODE_ANCHORS[idx] as number]
    let endAnchorIdx = idx + 1
    while (
      endAnchorIdx < segments.length - 1 &&
      (props.nodeKeys[endAnchorIdx] ?? []).length === 0
    ) {
      anchors.push(NODE_ANCHORS[endAnchorIdx] as number)
      endAnchorIdx++
    }
    if (endAnchorIdx < NODE_ANCHORS.length) anchors.push(NODE_ANCHORS[endAnchorIdx] as number)
    return anchors
  }

  function handleMove(e: MouseEvent | TouchEvent) {
    if (isDragging.value === null || !pathRef.value) return

    const svg = pathRef.value.ownerSVGElement!
    const pt = svg.createSVGPoint()
    pt.x = 'touches' in e ? e.touches[0].clientX : (e as MouseEvent).clientX
    pt.y = 'touches' in e ? e.touches[0].clientY : (e as MouseEvent).clientY

    const svgMatrix = svg.getScreenCTM()
    if (!svgMatrix) return

    const svgPt = pt.matrixTransform(svgMatrix.inverse())
    const idx = isDragging.value
    const startRange = NODE_ANCHORS[idx] + 0.01
    const endRange = getMaxReachable(idx)

    let minBoardDist = Infinity
    let bestProgress = startRange

    for (let i = 0; i <= 400; i++) {
      const p = startRange + (i / 400) * (endRange - startRange)
      const point = pathRef.value.getPointAtLength(p * pathLength.value)
      const dist = Math.pow(point.x - svgPt.x, 2) + Math.pow(point.y - svgPt.y, 2)
      if (dist < minBoardDist) {
        minBoardDist = dist
        bestProgress = p
      }
    }

    const next = [...progress.value]
    next[idx] = bestProgress
    progress.value = next
  }

  function handleUp() {
    if (isDragging.value === null) return

    const idx = isDragging.value
    const currentP = progress.value[idx]
    const snapAnchors = getSnapAnchors(idx)

    let snappedAnchorIdx = 0
    for (let i = 0; i < snapAnchors.length - 1; i++) {
      const left = snapAnchors[i]
      const right = snapAnchors[i + 1]
      const rightTarget = right - 0.16
      if (currentP <= rightTarget) {
        const mid = (left + rightTarget) / 2
        snappedAnchorIdx = currentP >= mid ? i + 1 : i
        break
      }
      snappedAnchorIdx = i + 1
    }

    let finalP: number
    if (snappedAnchorIdx === 0) {
      finalP = snapAnchors[0]
    } else {
      const targetAnchor = snapAnchors[snappedAnchorIdx]
      const nodeIdx = NODE_ANCHORS.indexOf(targetAnchor as (typeof NODE_ANCHORS)[number])
      if (nodeIdx > idx) {
        const endRange = targetAnchor
        finalP = Math.max(NODE_ANCHORS[idx] + 0.01, endRange - 0.16)
      } else {
        finalP = getMaxReachable(idx)
      }
    }

    const next = [...progress.value]
    next[idx] = finalP
    progress.value = next

    isDragging.value = null
    removeListeners()
  }

  function addListeners() {
    window.addEventListener('mousemove', handleMove)
    window.addEventListener('mouseup', handleUp)
  }

  function removeListeners() {
    window.removeEventListener('mousemove', handleMove)
    window.removeEventListener('mouseup', handleUp)
  }

  function resetProgress() {
    progress.value = [...NODE_ANCHORS]
  }

  function setProgress(values: number[]) {
    if (!Array.isArray(values) || values.length !== NODE_ANCHORS.length) return
    progress.value = [...values]
  }

  function getKeyLabel(keywords: string) {
    const config = KEYCODE_KEYWORDS_MAP.get(keywords)

    if (config) {
      return getLocalizedField(config.alias || config.name)
    }

    return ''
  }

  defineExpose({
    progress,
    NODE_ANCHORS,
    resetProgress,
    setProgress,
    coveredNodeSet,
  })
</script>

<style scoped></style>
